import * as deepmerge from 'deepmerge';
import { isNil } from 'lodash-es';
import * as errors from '../errors';
import * as resources from '../resources';
import createRequestPath from './createRequestPath';
import defaultCtx from './defaultCtx';
import * as setup from './setup';
const getFullCtx = (getClientCtx) => (requestCtx) => deepmerge.all([defaultCtx, getClientCtx(), requestCtx || {}]);
export const createRequesterBuilder = (getClientCtx) => {
    const requesterBuilder = ({ path, requestCtx, config: cacheConfig, keysToInvalidate, patternToDelete, patternToInvalidate }) => {
        /** Need to be redeclared on each request */
        const fullCtx = getFullCtx(getClientCtx)(requestCtx);
        const { pathWithoutQueryParam, fullPath } = createRequestPath(fullCtx, path);
        async function handleError(error) {
            console.log('Unexpected error in client', JSON.stringify(error));
            const parsedError = await errors.handleError(error);
            const errorToThrow = fullCtx.fromError ? fullCtx.fromError(parsedError) : parsedError;
            throw errorToThrow;
        }
        async function fetchAndTransform(pathToFetch, body) {
            try {
                const { fetcher, parse, transform, 
                // Those two variables are destructured but not used because they are not part of the request init
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                protocol, 
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                host, ...requestInit } = fullCtx;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { ['content-type']: _a, ...headersWithoutContentType } = requestInit.headers
                    ? 'content-type' in requestInit.headers
                        ? requestInit.headers
                        : { ...requestInit.headers, 'content-type': '' }
                    : { 'content-type': '' };
                //this code should be remove as soon as backend will be able to provide consistent URL
                //at the time of writing backend is behind an nginx reverse proxy that mess with path (aka path based routing)
                //so url given by backend don't include this part of path so we have to "fix" those url by replacing with expected on on this endpoint
                let finalPathToFetch = pathToFetch;
                const [pathToFetchWithoutQP, QPToFetch] = pathToFetch.split('?');
                if (pathToFetchWithoutQP !== pathWithoutQueryParam) {
                    finalPathToFetch = `${pathWithoutQueryParam}?${QPToFetch}`;
                }
                const finalHeaders = (body ? requestInit.headers : headersWithoutContentType);
                const response = await fetcher(finalPathToFetch, {
                    ...requestInit,
                    headers: finalHeaders,
                    credentials: host === requestInit.hosts.tiles ? 'include' : undefined,
                    body
                });
                if (!response.ok && response.status / 100 >= 4) {
                    throw new errors.FetchError({
                        status: response.status,
                        response: { body: response },
                        url: response.url
                    });
                }
                const parsedResponse = await parse(response);
                return transform(parsedResponse);
            }
            catch (error) {
                // without return promise fail silently
                return handleError(error);
            }
        }
        const request = async (keyOrNewValue) => {
            /** In case of query newValue would be string (ie path), in case of mutation it will be an object we would transform to a raw object */
            const newValue = fullCtx.method !== 'GET' ? keyOrNewValue : undefined;
            try {
                const { params, toRaw, bodySerializer } = fullCtx;
                const body = newValue !== undefined
                    ? bodySerializer(toRaw(newValue))
                    : params?.body
                        ? bodySerializer(params.body)
                        : undefined;
                return fetchAndTransform(fullPath, body);
            }
            catch (error) {
                // without return promise fail silently
                return handleError(error);
            }
        };
        return {
            key: [
                `${fullPath}${!isNil(fullCtx.headers?.['x-weenat-login-as'])
                    ? `#${fullCtx.headers?.['x-weenat-login-as']}`
                    : ''}`
            ],
            invalidationKey: [pathWithoutQueryParam],
            request,
            cacheConfig,
            keysToInvalidate,
            patternToDelete,
            fetchAndTransform,
            patternToInvalidate
        };
    };
    return requesterBuilder;
};
const buildClient = (requesterBuilder, config) => ({
    // TODO: find a way to do an Object.entries and give the requester builder to every resource with proper typings
    actura: resources.actura(requesterBuilder, config),
    alerts: resources.alerts(requesterBuilder, config),
    api: resources.api(requesterBuilder, config),
    auth: resources.auth(requesterBuilder, config),
    billing: resources.billing(requesterBuilder, config),
    boundingBoxes: resources.boundingBoxes(requesterBuilder, config),
    charts: resources.charts(requesterBuilder, config),
    crops: resources.crops(requesterBuilder, config),
    cumulativeSum: resources.cumulativeSum(requesterBuilder, config),
    cycles: resources.cycles(requesterBuilder, config),
    decitrait: resources.decitrait(requesterBuilder, config),
    devices: resources.devices(requesterBuilder, config),
    externalConnectionsError: resources.externalConnectionsError(requesterBuilder, config),
    forecasts: resources.forecasts(requesterBuilder, config),
    horizons: resources.horizons(requesterBuilder, config),
    invitations: resources.invitations(requesterBuilder, config),
    irritensio: resources.irritensio(requesterBuilder, config),
    maizy: resources.maizy(requesterBuilder, config),
    me: resources.me(requesterBuilder, config),
    measurements: resources.measurements(requesterBuilder, config),
    measurementsConfig: resources.measurementsConfig(requesterBuilder, config),
    measurementsReports: resources.measurementsReports(requesterBuilder, config),
    members: resources.members(requesterBuilder, config),
    mobileDevices: resources.mobileDevices(requesterBuilder, config),
    networkOrgs: resources.networkOrgs(requesterBuilder, config),
    myFarm: resources.myFarm(requesterBuilder, config),
    networks: resources.networks(requesterBuilder, config),
    notifications: resources.notifications(requesterBuilder, config),
    options: resources.options(requesterBuilder, config),
    orgNetworks: resources.orgNetworks(requesterBuilder, config),
    orgOptionActivations: resources.orgOptionActivations(requesterBuilder, config),
    orgs: resources.orgs(requesterBuilder, config),
    plotOptionActivations: resources.plotOptionActivations(requesterBuilder, config),
    plots: resources.plots(requesterBuilder, config),
    preferences: resources.preferences(requesterBuilder, config),
    radar: resources.radar(requesterBuilder, config),
    reverseGeoCoding: resources.reverseGeocoding(requesterBuilder, config),
    seeds: resources.seeds(requesterBuilder, config),
    smag: resources.smag(requesterBuilder, config),
    stages: resources.stages(requesterBuilder, config),
    stationOptionActivations: resources.stationOptionActivations(requesterBuilder, config),
    stations: resources.stations(requesterBuilder, config),
    tavmills: resources.tavmills(requesterBuilder, config),
    thresholds: resources.thresholds(requesterBuilder, config),
    tours: resources.tours(requesterBuilder, config),
    vpd: resources.vpd(requesterBuilder, config),
    waterBalance: resources.waterBalance(requesterBuilder, config),
    waterBalanceArvalis: resources.waterBalanceArvalis(requesterBuilder, config),
    weephyt: resources.weephyt(requesterBuilder, config)
});
const Client = (
//TODO:  Can not make hosts required (use SetRequired from type-fest)
getClientCtx, options) => {
    if (options) {
        if (options.isNative)
            setup.native(options.appState);
    }
    const requesterBuilder = createRequesterBuilder(getClientCtx);
    return buildClient(requesterBuilder, {
        getCtx: getFullCtx(getClientCtx)
    });
};
export default Client;
const clientBuildedToExtractType = () => buildClient(() => ({
    invalidationKey: [''],
    key: [''],
    request: async () => '',
    fetchAndTransform: async () => ''
}), { getCtx: getFullCtx(() => ({})) });
